import React, { useState } from 'react';
import { Clock, MessageSquare, CheckCircle, Send } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Card } from './ui/card';
import { toast } from 'sonner';
import {
  WIDGET_MUTED_TEXT_CLASS,
  WIDGET_PANEL_CLASS,
} from '../constants/widgetTheme';

interface QuickActionsBarProps {
  onLogTime: (hours: number, description: string) => Promise<void>;
  onAddNote: (note: string) => Promise<void>;
  onUpdateStatus: (status: string) => Promise<void>;
  currentStatus: string;
  estimatedHours?: number;
  sticky?: boolean;
}

export default function QuickActionsBar({
  onLogTime,
  onAddNote,
  onUpdateStatus,
  currentStatus,
  estimatedHours = 0,
  sticky = true,
}: QuickActionsBarProps) {
  const [activeTab, setActiveTab] = useState<'time' | 'note' | 'status'>('time');
  const [hours, setHours] = useState('');
  const [description, setDescription] = useState('');
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(false);

  const handleQuickLog = async () => {
    if (!hours || parseFloat(hours) <= 0) {
      toast.error('Please enter valid hours');
      return;
    }

    setLoading(true);
    try {
      await onLogTime(parseFloat(hours), description);
      setHours('');
      setDescription('');
      toast.success(`Logged ${hours} hours`);
    } catch (error) {
      toast.error('Failed to log time');
    } finally {
      setLoading(false);
    }
  };

  const handleQuickNote = async () => {
    if (!note.trim()) {
      toast.error('Please enter a note');
      return;
    }

    setLoading(true);
    try {
      await onAddNote(note);
      setNote('');
      toast.success('Note added');
    } catch (error) {
      toast.error('Failed to add note');
    } finally {
      setLoading(false);
    }
  };

  const handleQuickStatus = async (status: string) => {
    setLoading(true);
    try {
      await onUpdateStatus(status);
      toast.success(`Status updated to ${status}`);
    } catch (error) {
      toast.error('Failed to update status');
    } finally {
      setLoading(false);
    }
  };

  const statuses = ['Not Started', 'In Progress', 'In Review', 'Blocked', 'Done'];

  return (
    <Card className={`${WIDGET_PANEL_CLASS} ${sticky ? 'sticky top-0 z-10 sm:top-4' : ''} shadow-lg`}>
      <div className="p-2 sm:p-4 space-y-3">
        {/* Quick Tab Selector */}
        <div className="flex gap-1 sm:gap-2 overflow-x-auto pb-1">
          <Button
            size="sm"
            variant={activeTab === 'time' ? 'default' : 'outline'}
            onClick={() => setActiveTab('time')}
            className="flex-1 min-w-[70px] h-9 sm:h-8 text-xs sm:text-sm"
          >
            <Clock className="w-4 h-4 mr-1" />
            <span className="hidden sm:inline">Log</span> Time
          </Button>
          <Button
            size="sm"
            variant={activeTab === 'note' ? 'default' : 'outline'}
            onClick={() => setActiveTab('note')}
            className="flex-1 min-w-[70px] h-9 sm:h-8 text-xs sm:text-sm"
          >
            <MessageSquare className="w-4 h-4 mr-1" />
            <span className="hidden sm:inline">Add</span> Note
          </Button>
          <Button
            size="sm"
            variant={activeTab === 'status' ? 'default' : 'outline'}
            onClick={() => setActiveTab('status')}
            className="flex-1 min-w-[70px] h-9 sm:h-8 text-xs sm:text-sm"
          >
            <CheckCircle className="w-4 h-4 mr-1" />
            Status
          </Button>
        </div>

        {/* Quick Time Log */}
        {activeTab === 'time' && (
          <div className="space-y-2 sm:space-y-3 animate-in fade-in-50 duration-200">
            <div className="flex gap-2 flex-col sm:flex-row">
              <div className="flex-1 relative">
                <Input
                  type="number"
                  step="0.5"
                  min="0"
                  max="24"
                  value={hours}
                  onChange={(e) => setHours(e.target.value)}
                  placeholder="Hours (e.g., 2.5)"
                  className="pr-12 h-10 sm:h-9 text-base"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleQuickLog();
                    }
                  }}
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                  / {estimatedHours}h
                </span>
              </div>
              <Button size="sm" onClick={handleQuickLog} disabled={loading || !hours} className="h-10 sm:h-9">
                <Send className="w-4 h-4" />
              </Button>
            </div>
            <Input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What did you work on? (optional)"
              className="text-sm h-10 sm:h-9"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleQuickLog();
                }
              }}
            />
            {/* Quick hour buttons */}
            <div className="grid grid-cols-3 sm:flex sm:gap-1 sm:flex-wrap gap-2">
              {[0.5, 1, 2, 4, 8].map((h) => (
                <Button
                  key={h}
                  size="sm"
                  variant="ghost"
                  onClick={() => setHours(h.toString())}
                  className="text-xs sm:text-xs sm:px-2 sm:h-7 h-10 font-medium"
                >
                  {h}h
                </Button>
              ))}
            </div>
          </div>
        )}

        {/* Quick Note */}
        {activeTab === 'note' && (
          <div className="space-y-2 sm:space-y-3 animate-in fade-in-50 duration-200">
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Add standup note, progress update, or blocker..."
              rows={4}
              className="text-sm sm:text-sm resize-none h-28 sm:h-24 text-base"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                  e.preventDefault();
                  handleQuickNote();
                }
              }}
            />
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
              <span className={`text-xs ${WIDGET_MUTED_TEXT_CLASS}`}>
                Press {navigator.platform.includes('Mac') ? '⌘' : 'Ctrl'}+Enter to submit
              </span>
              <Button size="sm" onClick={handleQuickNote} disabled={loading || !note.trim()} className="w-full sm:w-auto h-10 sm:h-9">
                <Send className="w-4 h-4 mr-1" />
                Add Note
              </Button>
            </div>
          </div>
        )}

        {/* Quick Status Update */}
        {activeTab === 'status' && (
          <div className="space-y-2 sm:space-y-3 animate-in fade-in-50 duration-200">
            <div className={`mb-2 text-xs ${WIDGET_MUTED_TEXT_CLASS}`}>Current: <strong>{currentStatus}</strong></div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {statuses.map((status) => (
                <Button
                  key={status}
                  size="sm"
                  variant={currentStatus === status ? 'default' : 'outline'}
                  onClick={() => handleQuickStatus(status)}
                  disabled={loading || currentStatus === status}
                  className="text-xs sm:text-xs h-10 sm:h-9 font-medium"
                >
                  {status}
                </Button>
              ))}
            </div>
          </div>
        )}
      </div>
    </Card>
  );
}
